import * as React from 'react';
import { Box, Button, Chip, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import FitScreenRoundedIcon from '@mui/icons-material/FitScreenRounded';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import TypeUtil from '../QueryBuilder/typeahead.util.js';
import RemovePresetDialog from '../RemovePresetDialog';
import RenameScreener from '../RenameScreener';
import _getWideScreenerMetricsCount from '../../../../lib/screener/_getScreenerMetricsCount';
import { useRouter } from 'next/router.js';

const getScrollStripSx = (sidebarMode) =>
  sidebarMode
    ? { display: 'flex', gap: 0.75, flexWrap: 'wrap', pb: 0.5 }
    : { display: 'flex', gap: 1, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, pb: 0.5 };

const roundToNearestTen = (number) => {
  if (number >= 10) return Math.round(number / 10) * 10 + '+';
  if (number > 5) return '5+';
  if (number > 0) return '1+';
  return '';
};

function PresetPill({ preset, active, onClick, onContextMenu, defaultScreenerTableCols }) {
  const [{ criteria, screen_name }] = preset;
  const [_count, setCount] = React.useState(0);

  React.useEffect(() => {
    const formula = TypeUtil.getFormula(
      criteria?.replace(/[()"]/g, '')?.replace(/AND/g, 'AND\n')?.replace(/ and /g, ' AND\n')?.replace(/ or /g, ' OR '),
      defaultScreenerTableCols?.sort((a, b) => b?.name?.length - a?.name?.length)
    );
    if (formula?.length > 0) {
      _getWideScreenerMetricsCount('AND', formula)
        .then((d) => setCount(`${d?.getWideScreenerMetricsCountV3}`))
        .catch((error) => console.log(error));
    }
  }, []);

  const countLabel = roundToNearestTen(_count);

  return (
    <Tooltip title={screen_name.length > 24 ? screen_name : ''} placement="top" disableInteractive>
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{screen_name}</span>
            {countLabel && (
              <Box
                component="span"
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  color: active ? 'primary.main' : 'text.disabled',
                  ml: 0.25,
                }}
              >
                {countLabel}
              </Box>
            )}
          </Box>
        }
        onClick={onClick}
        onContextMenu={onContextMenu}
        sx={{
          height: 30, borderRadius: '8px', fontSize: '0.8rem', fontWeight: 500,
          textTransform: 'capitalize', whiteSpace: 'nowrap', cursor: 'pointer',
          flexShrink: 0, transition: 'all 0.15s ease',
          ...(active ? {
            bgcolor: 'rgba(25,118,210,0.1)', color: 'primary.main',
            border: '1px solid', borderColor: 'rgba(25,118,210,0.3)',
            fontWeight: 600,
            '&:hover': { bgcolor: 'rgba(25,118,210,0.15)' },
            '& .MuiChip-label': { color: 'primary.main' },
          } : {
            bgcolor: 'transparent', border: '1px solid rgba(0,0,0,0.1)', color: 'text.primary',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.2)' },
          }),
        }}
      />
    </Tooltip>
  );
}

export default function ScreenPresets({
  userPresets, setActivePresetName, setFormula, deletePreset, renamePreset, setActivePresetDesc, userUpdatedPreset,
  setPresetCriteria, defaultScreenerTableCols, setTableCols, userDeletedPreset, setActivePresetCode, activePresetName, presetCriteria,
  isPremium, setBanner, sidebarMode = false,
}) {
  const router = useRouter();
  const [showAllPopular, setShowAllPopular] = React.useState(false);
  const [contextMenu, setContextMenu] = React.useState(null);
  const [contextPreset, setContextPreset] = React.useState(null);
  const [presetDialog, setpresetDialog] = React.useState(false);
  const [renameDialog, setRenameDialog] = React.useState(false);

  const handlePreset = (preset) => {
    let [{ id, isSystemDefault, code, criteria, screen_name, screen_desc, columns, count }] = preset;
    setActivePresetName(screen_name);
    setActivePresetDesc(screen_desc);
    setActivePresetCode(code);
    setPresetCriteria(criteria?.replace(/[()"]/g, '')?.replace(/AND/g, 'AND\n')?.replace(/ and /g, ' AND\n')?.replace(/ or /g, ' OR '));
    if (!columns?.includes('ticker') && !columns?.includes('tickername')) {
      columns = columns?.replace('[', "['ticker','tickername',");
    } else if (!columns?.includes('ticker')) {
      columns = columns?.replace('[', "['ticker',");
    } else if (!columns?.includes('tickername')) {
      columns = columns?.replace('[', "['tickername',");
    }
    setTableCols(columns && columns.length > 0 ? JSON.parse(columns?.replace(/\|/g, '__')?.replace(/\./g, '_')?.replace(/'/g, '"'))?.map(field => defaultScreenerTableCols?.find(item => item?.field === field)) : [] ?? []);
    if (criteria !== "") {
      setFormula(TypeUtil.getFormula(criteria?.replace(/[()"]/g, '')?.replace(/AND/g, 'AND\n')?.replace(/ and /g, ' AND\n')?.replace(/ or /g, ' OR '), defaultScreenerTableCols?.sort((a, b) => b?.name?.length - a?.name?.length)));
    }
  };

  const isActive = (preset) => {
    const [{ criteria }] = preset;
    return presetCriteria === criteria?.replace(/[()"]/g, '')?.replace(/AND/g, 'AND\n')?.replace(/ and /g, ' AND\n')?.replace(/ or /g, ' OR ');
  };

  const handleUserPresetNav = (preset) => {
    const [{ code, criteria, screen_name }] = preset;
    router.push({ pathname: `/stock-screener/u/${code}`, query: { query: criteria, name: screen_name } });
  };

  const handleContextMenu = (event, preset) => {
    event.preventDefault();
    setContextPreset(preset);
    setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null);
  };

  const handleContextClose = () => setContextMenu(null);
  const handleRenameClose = () => setRenameDialog(false);

  const groupedPresets = userPresets?.reduce((groups, preset) => {
    const { screen_name, isSystemDefault } = preset;
    const groupKey = isSystemDefault ? 'true' : 'false';
    if (!groups[groupKey]) groups[groupKey] = {};
    if (!groups[groupKey][screen_name]) groups[groupKey][screen_name] = [];
    groups[groupKey][screen_name].push(preset);
    return groups;
  }, {});

  const popularPresets = groupedPresets?.['true'] ? Object.entries(groupedPresets['true']) : [];
  const userPresetEntries = groupedPresets?.['false'] ? Object.entries(groupedPresets['false']) : [];
  const visiblePopularCount = 6;
  const hasMore = popularPresets.length > visiblePopularCount;
  const displayedPopular = showAllPopular ? popularPresets : popularPresets.slice(0, visiblePopularCount);
  const contextPresetData = contextPreset ? contextPreset[0] : null;

  if (popularPresets.length === 0 && userPresetEntries.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {popularPresets.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="overline"
            sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', color: 'text.disabled', display: 'block', mb: 0.75 }}>
            Popular Screens
          </Typography>
          <Box sx={getScrollStripSx(sidebarMode)}>
            {displayedPopular.map(([presetName, preset]) => (
              <PresetPill
                key={preset[0]?.code || presetName}
                preset={preset}
                active={isActive(preset)}
                onClick={() => handlePreset(preset)}
                defaultScreenerTableCols={defaultScreenerTableCols}
              />
            ))}
            {hasMore && !showAllPopular && (
              <Chip
                label={`+${popularPresets.length - visiblePopularCount} more`}
                onClick={() => setShowAllPopular(true)}
                sx={{
                  height: 28, borderRadius: '8px', fontSize: '0.75rem', fontWeight: 500,
                  whiteSpace: 'nowrap', cursor: 'pointer', bgcolor: 'transparent', flexShrink: 0,
                  border: '1px dashed rgba(0,0,0,0.15)', color: 'text.disabled',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              />
            )}
          </Box>
          <Box sx={{ mt: 1, mb: 0.25 }}>
            <Button size="small"
              sx={{ textTransform: 'none', color: 'text.disabled', fontSize: '0.78rem', fontWeight: 400, px: 0.5, '&:hover': { color: 'text.secondary' } }}
              href="/stock-screener/guide"
              endIcon={<ChevronRightRoundedIcon sx={{ fontSize: '0.9rem !important' }} />}>
              Screening guide
            </Button>
          </Box>
          <Divider sx={{ mb: 1.5, borderColor: 'rgba(0,0,0,0.06)' }} />
        </Box>
      )}

      {userPresetEntries.length > 0 ? (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="overline"
            sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', color: 'text.disabled', display: 'block', mb: 0.75 }}>
            Your Screens
          </Typography>
          <Box sx={getScrollStripSx(sidebarMode)}>
            {userPresetEntries.map(([presetName, preset]) => (
              <PresetPill
                key={preset[0]?.code || presetName}
                preset={preset}
                active={isActive(preset)}
                onClick={() => handleUserPresetNav(preset)}
                onContextMenu={(e) => handleContextMenu(e, preset)}
                defaultScreenerTableCols={defaultScreenerTableCols}
              />
            ))}
          </Box>
        </Box>
      ) : (
        popularPresets.length > 0 && (
          <Box sx={{
            border: '1px dashed rgba(0,0,0,0.1)', borderRadius: '10px',
            py: 2, px: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, mb: 2,
          }}>
            <BookmarkBorderRoundedIcon sx={{ fontSize: '1.2rem', color: 'text.disabled' }} />
            <Box>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled', lineHeight: 1.4 }}>
                Save your first screen to access it here
              </Typography>
              <Button size="small"
                sx={{ textTransform: 'none', fontSize: '0.78rem', p: 0, minWidth: 0, mt: 0.25, color: 'text.secondary' }}
                href="/stock-screener/guide"
                endIcon={<ChevronRightRoundedIcon sx={{ fontSize: '0.9rem' }} />}>
                Learn how
              </Button>
            </Box>
          </Box>
        )
      )}

      <Menu open={contextMenu !== null} onClose={handleContextClose}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}>
        <MenuItem dense onClick={() => { handleContextClose(); if (contextPreset) handlePreset(contextPreset); }}>
          <ListItemIcon><PlayArrowRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Quick View</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => { handleContextClose(); if (contextPreset) handleUserPresetNav(contextPreset); }}>
          <ListItemIcon><FitScreenRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Screen</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => { handleContextClose(); setRenameDialog(true); }}>
          <ListItemIcon><DriveFileRenameOutlineRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Rename Screen</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem dense onClick={() => { handleContextClose(); setpresetDialog(true); }}>
          <ListItemIcon><DeleteForeverRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Delete Screen</ListItemText>
        </MenuItem>
      </Menu>

      {contextPresetData && (
        <RemovePresetDialog
          deletePreset={deletePreset}
          presetDialog={presetDialog}
          setpresetDialog={setpresetDialog}
          presetName={contextPresetData.screen_name}
          presetCode={contextPresetData.code}
          userDeletedPreset={userDeletedPreset}
        />
      )}
      {contextPresetData && (
        <RenameScreener
          renamePreset={renamePreset}
          renameDialog={renameDialog}
          handleRenameClose={handleRenameClose}
          userUpdatedPreset={userUpdatedPreset}
          activePresetName={contextPresetData.screen_name}
          activePresetDesc={contextPresetData.screen_desc}
          activePresetCode={contextPresetData.code}
        />
      )}
    </Box>
  );
}
